import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Pagination from '../components/Pagination';
import '../styles/DashboardPage.css';
import { formatPrice } from '../utils/formatPrice';

const DashboardPage = () => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [processingOrders, setProcessingOrders] = useState([]);
  const [stats, setStats] = useState({
    totalSales: 0,
    totalRevenue: 0,
    activeSales: 0,
  });
  const [currentPendingPage, setCurrentPendingPage] = useState(1);
  const [pendingItemsPerPage] = useState(5);
  const [currentProcessingPage, setCurrentProcessingPage] = useState(1);
  const [processingItemsPerPage] = useState(5);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [pendingRes, processingRes, statsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/orders`, {
            params: { status: 'PENDING' },
            withCredentials: true // Use cookies for authentication
          }),
          axios.get(`${API_BASE_URL}/orders`, {
            params: { status: 'PROCESSING' },
            withCredentials: true // Use cookies for authentication
          }),
          axios.get(`${API_BASE_URL}/stats`, {
            withCredentials: true // Use cookies for authentication
          })
        ]);

        setPendingOrders(pendingRes.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        setProcessingOrders(processingRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error('Greška pri učitavanju podataka:', error);
        setError('Neuspešno učitavanje podataka. Molimo pokušajte ponovo.');

        if (error.response?.status === 401) {
          setError('Nemate dozvolu za pristup. Molimo prijavite se.');
        } else if (error.response?.status === 403) {
          setError('Nemate dozvolu za pristup ovom resursu.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [API_BASE_URL]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API_BASE_URL}/orders/admin/${orderId}`,
        { status: newStatus },
        {
          withCredentials: true, // Use cookies for authentication
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // Refresh data after successful update
      const [pendingRes, processingRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/orders`, {
          params: { status: 'PENDING' },
          withCredentials: true
        }),
        axios.get(`${API_BASE_URL}/orders`, {
          params: { status: 'PROCESSING' },
          withCredentials: true
        }),
        axios.get(`${API_BASE_URL}/stats`, {
          withCredentials: true
        })
      ]);

      setPendingOrders(pendingRes.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      setProcessingOrders(processingRes.data);
      setStats(statsRes.data);

    } catch (error) {
      console.error('Greška pri ažuriranju statusa:', error);
      alert(`Ažuriranje nije uspelo: ${error.response?.data?.message || error.message}`);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="dashboard-container">
        <div className="loading-spinner">
          <p>Učitavanje podataka...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>
            Pokušaj ponovo
          </button>
        </div>
      </div>
    );
  }

  // Pagination
  const indexOfLastPending = currentPendingPage * pendingItemsPerPage;
  const indexOfFirstPending = indexOfLastPending - pendingItemsPerPage;
  const currentPendingOrders = pendingOrders.slice(indexOfFirstPending, indexOfLastPending);

  const indexOfLastProcessing = currentProcessingPage * processingItemsPerPage;
  const indexOfFirstProcessing = indexOfLastProcessing - processingItemsPerPage;
  const currentProcessingOrders = processingOrders.slice(indexOfFirstProcessing, indexOfLastProcessing);

  return (
    <div className="dashboard-container">
      {/* Statistics section */}
      <section className="dashboard-section">
        <h2>Statistika</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Ukupna Prodaja</h3>
            <p>{stats.totalSales || 0}</p>
            <small>(Isporučene narudžbine)</small>
          </div>
          <div className="stat-card">
            <h3>Ukupan Prihod</h3>
            <p>{formatPrice(stats.totalRevenue)} RSD</p>
            <small>(Od isporučenih narudžbina)</small>
          </div>
          <div className="stat-card">
            <h3>Aktivne Prodaje</h3>
            <p>{stats.activeSales || 0}</p>
            <small>(Narudžbine u obradi)</small>
          </div>
        </div>
      </section>

      {/* Pending orders section */}
      <section className="dashboard-section">
        <h2>Narudžbine na Čekanju ({pendingOrders.length})</h2>
        <div className="orders-section">
          {currentPendingOrders.length === 0 ? (
            <div className="no-orders">Nema narudžbina na čekanju</div>
          ) : (
            <div className="orders-table">
              <table>
                <thead>
                  <tr>
                    <th>ID Narudžbine</th>
                    <th>Kupac</th>
                    <th>E-mail</th>
                    <th>Proizvodi</th>
                    <th>Cena</th>
                    <th>Maloprodajna cena</th>
                    <th>Ukupno</th>
                    <th>Status</th>
                    <th>Datum</th>
                    <th>Ažuriraj Status</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPendingOrders.map((order) => (
                    <tr key={order.id}>
                      <td>#{order.id}</td>
                      <td>
                        {`${order.user?.firstName || order.guestFirstName || 'N/A'} ${
                          order.user?.lastName || order.guestLastName || ''
                        }`.trim()}
                      </td>
                            <td>
                              {order.user?.email || order.guestEmail || 'N/A'}
                            </td>
                      <td>
                        <ul className="order-items-list">
                          {order.items?.map((item, index) => (
                            <li key={index}>
                              {item.productName} (x{item.quantity})
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td>
                        <ul className="order-items-list">
                          {order.items?.map((item, index) => (
                            <li key={index}>
                              {formatPrice(item.price)} RSD
                              {item.isOnSale && (
                                <span className="discount-badge">
                                  {item.discountPercentage}% OFF
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td>
                        <ul className="order-items-list">
                          {order.items?.map((item, index) => (
                            <li key={index}>
                              {formatPrice(item.retailPrice)} RSD
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td>{formatPrice(order.totalPrice)} RSD</td>
                      <td>
                        <span className={`status-badge ${order.status?.toLowerCase() || 'unknown'}`}>
                          {order.status || 'Nepoznato'}
                        </span>
                      </td>
                      <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                      <td>
                        <select
                          value={order.status || ''}
                          onChange={(e) => handleStatusChange(order.id, e.target.value)}
                        >
                          <option value="PENDING">Na čekanju</option>
                          <option value="PROCESSING">U obradi</option>
                          <option value="SHIPPED">Poslato</option>
                          <option value="DELIVERED">Isporučeno</option>
                          <option value="CANCELLED">Otkazano</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <Pagination
          currentPage={currentPendingPage}
          totalPages={Math.ceil(pendingOrders.length / pendingItemsPerPage)}
          onPageChange={setCurrentPendingPage}
        />
      </section>

      {/* Processing orders section */}
      <section className="dashboard-section">
        <h2>Aktivne Prodaje (Narudžbine u Obradi) ({processingOrders.length})</h2>
        <div className="orders-section">
          {currentProcessingOrders.length === 0 ? (
            <div className="no-orders">Nema narudžbina u obradi</div>
          ) : (
            <div className="orders-table">
              <table>
                <thead>
                  <tr>
                    <th>ID Narudžbine</th>
                    <th>Kupac</th>
                    <th>Proizvodi</th>
                    <th>Cena</th>
                    <th>Maloprodajna cena</th>
                    <th>Ukupno</th>
                    <th>Status</th>
                    <th>Datum</th>
                    <th>Ažuriraj Status</th>
                  </tr>
                </thead>
                <tbody>
                  {currentProcessingOrders.map((order) => (
                    <tr key={order.id}>
                      <td>#{order.id}</td>
                      <td>
                        {`${order.user?.firstName || order.guestFirstName || 'N/A'} ${
                          order.user?.lastName || order.guestLastName || ''
                        }`.trim()}
                      </td>
                      <td>
                        <ul className="order-items-list">
                          {order.items?.map((item, index) => (
                            <li key={index}>
                              {item.productName} (x{item.quantity})
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td>
                        <ul className="order-items-list">
                          {order.items?.map((item, index) => (
                            <li key={index}>
                              {formatPrice(item.price)} RSD
                              {item.isOnSale && (
                                <span className="discount-badge">
                                  {item.discountPercentage}% OFF
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td>
                        <ul className="order-items-list">
                          {order.items?.map((item, index) => (
                            <li key={index}>
                              {formatPrice(item.retailPrice)} RSD
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td>{formatPrice(order.totalPrice)} RSD</td>
                      <td>
                        <span className={`status-badge ${order.status?.toLowerCase() || 'unknown'}`}>
                          {order.status || 'Nepoznato'}
                        </span>
                      </td>
                      <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                      <td>
                        <select
                          value={order.status || ''}
                          onChange={(e) => handleStatusChange(order.id, e.target.value)}
                        >
                          <option value="PROCESSING">U obradi</option>
                          <option value="SHIPPED">Poslato</option>
                          <option value="DELIVERED">Isporučeno</option>
                          <option value="CANCELLED">Otkazano</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <Pagination
          currentPage={currentProcessingPage}
          totalPages={Math.ceil(processingOrders.length / processingItemsPerPage)}
          onPageChange={setCurrentProcessingPage}
        />
      </section>
    </div>
  );
};

export default DashboardPage;